import { ENV } from '../../config/runtimeEnv';
import { ScheduleOverride, EffectiveScheduleResponse, PractitionerAvailability } from "../../types/admin/TeamTypes";
import type { UserUuid } from "../../types/identity";
import { http } from "../../lib/http/client";

const API_BASE_URL = ENV.VITE_HEALTHCARE_SERVER;

const getAuthHeaders = () => ({
	"Content-Type": "application/json",
});

export const fetchScheduleOverrides = async (
	userId: UserUuid,
	startDate?: string,
	endDate?: string
): Promise<{ data: ScheduleOverride[]; count: number }> => {
	const params: any = {};
	if (startDate) params.start_date = startDate;
	if (endDate) params.end_date = endDate;

	const response = await http.get(`${API_BASE_URL}/practitioner/${userId}/schedule-overrides`, {
		params,
		headers: getAuthHeaders(),
	});

	return response.data;
};

export const getScheduleOverride = async (userId: UserUuid, overrideId: number): Promise<ScheduleOverride> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/${userId}/schedule-overrides/${overrideId}`, {
		headers: getAuthHeaders(),
	});

	return response.data;
};

export const createScheduleOverride = async (userId: UserUuid, data: Partial<ScheduleOverride>): Promise<{ message: string; data: ScheduleOverride }> => {
	const response = await http.post(`${API_BASE_URL}/practitioner/${userId}/schedule-overrides`, data, {
		headers: getAuthHeaders(),
	});

	return response.data;
};

export const updateScheduleOverride = async (userId: UserUuid, overrideId: number, data: Partial<ScheduleOverride>): Promise<{ message: string; data: ScheduleOverride }> => {
	const response = await http.put(`${API_BASE_URL}/practitioner/${userId}/schedule-overrides/${overrideId}`, data, {
		headers: getAuthHeaders(),
	});

	return response.data;
};

export const deleteScheduleOverride = async (userId: UserUuid, overrideId: number): Promise<{ message: string }> => {
	const response = await http.delete(`${API_BASE_URL}/practitioner/${userId}/schedule-overrides/${overrideId}`, {
		headers: getAuthHeaders(),
	});

	return response.data;
};

export const bulkCreateScheduleOverrides = async (
	userId: UserUuid,
	overrides: Partial<ScheduleOverride>[]
): Promise<{
	message: string;
	created: number;
	errors: any[];
	data: ScheduleOverride[];
}> => {
	const response = await http.post(
		`${API_BASE_URL}/practitioner/${userId}/schedule-overrides/bulk`,
		{ overrides },
		{
			headers: getAuthHeaders(),
		}
	);

	return response.data;
};

export const getEffectiveSchedule = async (userId: UserUuid, date: string): Promise<EffectiveScheduleResponse> => {
	const response = await http.get(`${API_BASE_URL}/practitioner/${userId}/schedule-overrides/effective`, {
		params: { date },
		headers: getAuthHeaders(),
	});

	return response.data;
};

// One request for the whole roster — the per-practitioner fetch above was called in a loop.
export const fetchPractitionerAvailability = async (
	practitionerIds: UserUuid[],
	startDate: string,
	endDate: string
): Promise<PractitionerAvailability> => {
	const response = await http.get(`${API_BASE_URL}/practitioners/availability`, {
		params: {
			practitioner_ids: practitionerIds,
			start_date: startDate,
			end_date: endDate,
		},
		headers: getAuthHeaders(),
	});

	return response.data;
};
